/**
 * Analysis Modal
 *
 * Configure and launch EUDR compliance analysis for a polygon.
 * - Select baseline and monitoring years
 * - Option to generate PDF report
 * - Select report language (ES/EN)
 */

import { X, PlayCircle, Loader2, AlertCircle, Clock } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { analysisApi } from '../../lib/api';
import toast from 'react-hot-toast';

interface AnalysisModalProps {
    polygon: any;
    onClose: () => void;
    onSuccess: (jobId: string) => void;
}

export default function AnalysisModal({ polygon, onClose, onSuccess }: AnalysisModalProps) {
    const { t } = useTranslation();
    const currentYear = new Date().getFullYear();
    const maxYear = currentYear - 1;
    // Use previous year as default monitoring year (current year data may not be fully processed)
    const defaultMonitoringYear = Math.min(currentYear - 1, 2025);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        baseline_year: 2020,
        monitoring_year: defaultMonitoringYear,
        generate_report: true,
        report_language: 'en',
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (formData.baseline_year >= formData.monitoring_year) {
            toast.error(t('analysis.yearValidation.baselineBeforeMonitoring'));
            return;
        }

        if (formData.baseline_year < 2017 || formData.baseline_year > maxYear) {
            toast.error(t('analysis.yearValidation.baselineRange', { maxYear }));
            return;
        }

        if (formData.monitoring_year < 2017 || formData.monitoring_year > maxYear) {
            toast.error(t('analysis.yearValidation.monitoringRange', { maxYear }));
            return;
        }

        try {
            setSubmitting(true);

            // Create analysis job
            const job = await analysisApi.createJob({
                polygon_id: polygon.id,
                commodity: polygon.commodity,
                baseline_year: formData.baseline_year,
                monitoring_year: formData.monitoring_year,
                generate_report: formData.generate_report,
                report_language: formData.report_language,
            });

            toast.success(t('analysis.analysisStarted'));

            // Wait for onSuccess to complete before closing
            await onSuccess(job.id);

            onClose();
        } catch (error: any) {
            console.error('Analysis error:', error);
            console.error('Error response:', error.response);

            // Handle Pydantic validation errors (422)
            let errorMsg = t('analysis.analysisError');

            if (error.response?.data?.detail) {
                const detail = error.response.data.detail;

                // Check if detail is an array of validation errors (Pydantic format)
                if (Array.isArray(detail)) {
                    const errors = detail.map((err: any) => {
                        const field = err.loc ? err.loc.join('.') : 'unknown';
                        return `${field}: ${err.msg}`;
                    }).join(', ');
                    errorMsg = `${t('analysis.validationErrors')}: ${errors}`;
                } else if (typeof detail === 'string') {
                    errorMsg = detail;
                } else {
                    errorMsg = JSON.stringify(detail);
                }
            }

            toast.error(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData({ ...formData, [name]: checked });
        } else if (type === 'number') {
            setFormData({ ...formData, [name]: parseInt(value) });
        } else {
            setFormData({ ...formData, [name]: value });
        }
    };

    return (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
            <div className="card p-0 w-full max-w-2xl animate-slide-up">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300 flex items-center justify-center">
                            <PlayCircle className="w-5 h-5" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                {t('analysis.startAnalysis')}
                            </h2>
                            <p className="text-sm text-gray-500">{polygon.name}</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        disabled={submitting}
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {/* Info Alert */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-4 flex gap-3">
                        <AlertCircle className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-gray-700 dark:text-gray-300">
                            <p className="font-medium mb-1">{t('analysis.howItWorks')}</p>
                            <p>{t('analysis.howItWorksDescription')}</p>
                            <p className="mt-2 text-xs">
                                <strong>{t('analysis.howItWorksExample')}</strong>
                            </p>
                        </div>
                    </div>

                    {/* Years Configuration */}
                    <div className="space-y-4">
                        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                            {t('analysis.selectPeriod')}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="baseline_year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('analysis.baselineLabel')} *
                                </label>
                                <input
                                    type="number"
                                    id="baseline_year"
                                    name="baseline_year"
                                    value={formData.baseline_year}
                                    onChange={handleChange}
                                    min={2017}
                                    max={maxYear}
                                    required
                                    className="input w-full text-lg font-semibold"
                                    disabled={submitting}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('analysis.baselineHint')} (2017-{maxYear})
                                </p>
                            </div>

                            <div>
                                <label htmlFor="monitoring_year" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('analysis.monitoringLabel')} *
                                </label>
                                <input
                                    type="number"
                                    id="monitoring_year"
                                    name="monitoring_year"
                                    value={formData.monitoring_year}
                                    onChange={handleChange}
                                    min={formData.baseline_year + 1}
                                    max={maxYear}
                                    required
                                    className="input w-full text-lg font-semibold"
                                    disabled={submitting}
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {t('analysis.monitoringHint', { maxYear })}
                                </p>
                            </div>
                        </div>
                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                <strong>{t('analysis.comparisonNote', { baseline: formData.baseline_year, monitoring: formData.monitoring_year, maxYear })}</strong>
                            </p>
                        </div>
                    </div>

                    {/* Report Options */}
                    <div className="space-y-4">
                        <div className="flex items-center">
                            <input
                                type="checkbox"
                                id="generate_report"
                                name="generate_report"
                                checked={formData.generate_report}
                                onChange={handleChange}
                                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                                disabled={submitting}
                            />
                            <label htmlFor="generate_report" className="ml-3 text-sm font-medium text-gray-700 dark:text-gray-300">
                                {t('analysis.generatePdfAuto')}
                            </label>
                        </div>

                        {formData.generate_report && (
                            <div>
                                <label htmlFor="report_language" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('analysis.reportLanguage')}
                                </label>
                                <select
                                    id="report_language"
                                    name="report_language"
                                    value={formData.report_language}
                                    onChange={handleChange}
                                    className="input w-full"
                                    disabled={submitting}
                                >
                                    <option value="en">English</option>
                                    <option value="es">Español</option>
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Processing Info */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
                        <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                            {t('analysis.processingTitle')}
                        </p>
                        <p className="text-sm text-gray-700 dark:text-gray-300">
                            {t('analysis.processingWhatHappens')}
                        </p>
                        <div className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <Clock className="w-4 h-4 flex-shrink-0 mt-0.5 text-gray-500" />
                            <span>
                                <strong>{t('analysis.processingTime')}</strong>{' '}
                                {t('analysis.processingFactors')}
                            </span>
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t('analysis.processingBackground')}
                        </p>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onClose}
                            className="btn-secondary"
                            disabled={submitting}
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            type="submit"
                            className="btn-primary flex items-center gap-2"
                            disabled={submitting}
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    {t('analysis.submitting')}
                                </>
                            ) : (
                                <>
                                    <PlayCircle className="w-4 h-4" />
                                    {t('analysis.startButton')}
                                </>
                            )}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
